/** Cached directions response for offline reuse (provider-agnostic). */

export type RouteManeuverStep = {
  instruction: string;
  distanceM: number;
  durationS: number;
  location?: [number, number];
  type?: string;
  modifier?: string;
};

export type RouteDirectionsResult = {
  /** GeoJSON LineString coordinates [lng, lat][] */
  coordinates: Array<[number, number]>;
  distanceM: number;
  durationS: number;
  steps: RouteManeuverStep[];
  fetchedAt: number;
  profile: 'driving' | 'walking' | 'cycling';
  /** Adapter id (maptiler, …). */
  provider?: string;
  /** Stop ids used when requesting (for cache key). */
  stopIds?: string[];
};

export const ROUTE_DIRECTIONS_CACHE_KEY = 'alea.runner.lastDirections';

export function parseDirectionsResult(raw: unknown): RouteDirectionsResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const coords = Array.isArray(r.coordinates)
    ? (r.coordinates as unknown[])
      .filter((c): c is [number, number] => Array.isArray(c) && c.length >= 2
        && Number.isFinite(Number(c[0])) && Number.isFinite(Number(c[1])))
      .map((c) => [Number(c[0]), Number(c[1])] as [number, number])
    : [];
  if (!coords.length) return null;
  const steps = Array.isArray(r.steps)
    ? (r.steps as unknown[]).map((s) => {
      const step = (s && typeof s === 'object' ? s : {}) as Record<string, unknown>;
      return {
        instruction: String(step.instruction ?? ''),
        distanceM: Number(step.distanceM ?? 0),
        durationS: Number(step.durationS ?? 0),
        location: Array.isArray(step.location) && step.location.length >= 2
          ? [Number(step.location[0]), Number(step.location[1])] as [number, number]
          : undefined,
        type: step.type != null ? String(step.type) : undefined,
        modifier: step.modifier != null ? String(step.modifier) : undefined,
      };
    })
    : [];
  return {
    coordinates: coords,
    distanceM: Number(r.distanceM ?? 0),
    durationS: Number(r.durationS ?? 0),
    steps,
    fetchedAt: Number(r.fetchedAt ?? Date.now()),
    profile: (r.profile === 'walking' || r.profile === 'cycling' ? r.profile : 'driving'),
    provider: r.provider != null ? String(r.provider) : undefined,
    stopIds: Array.isArray(r.stopIds) ? r.stopIds.map(String) : undefined,
  };
}

export function readCachedDirections(
  storage?: Pick<Storage, 'getItem'> | null,
): RouteDirectionsResult | null {
  try {
    const store = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
    if (!store) return null;
    const raw = store.getItem(ROUTE_DIRECTIONS_CACHE_KEY);
    if (!raw) return null;
    return parseDirectionsResult(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedDirections(
  result: RouteDirectionsResult,
  storage?: Pick<Storage, 'setItem'> | null,
): void {
  try {
    const store = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
    store?.setItem(ROUTE_DIRECTIONS_CACHE_KEY, JSON.stringify(result));
  } catch {
    // ignore
  }
}
